<template>
    <div id="map"></div>    
</template>

<script>
/**
 * LocationsMap component contains a MapBox Map with zoom-out clustered 
 * locations points over the world map. 
 */
import mapboxgl from 'mapbox-gl';
mapboxgl.accessToken = 'pk.eyJ1IjoiZGVhcmRvYyIsImEiOiJja2sydnZ5MHUwbzJ4MnZvYmVjNHM4c3A3In0.dJe6opEAXZh7aEZHrUCxIg';

export default {
    name: 'LocationsMap',
    data: () => ({
        map: {},
        data: { // Default data object to prevent exceptions duringmapbox cold start
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            },
            cluster: true,
            clusterMaxZoom: 13,
            clusterRadius: 50,
            clusterProperties: {
                "total_chats": ["+", ["get", "chats"]]
            }
        }
    }),
    props: {
        dataSource: { // Watchable prop to update data on change
            type: Object,
            required: true,
        }
    },
    computed: {
        colors() {
            return this.$vuetify.theme.isDark ? 
                this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
        }
    },
    mounted() {
        this.initMap(this.dataSource); // Fire mapbox cold start
    },
    methods: {
        initMap(dataSource) {
            this.map = new mapboxgl.Map({
                container: 'map',
                style: 'mapbox://styles/lucasmenendev/cksbkt7uh25f517rz2v77zlqh',
                center: [-100, 35],
                zoom: 3,
                minZoom: 3,
                maxZoom: 4,
                preserveDrawingBuffer: true
            });

            this.map.on('load', () => {
                this.configureMap(dataSource);
            });
        },
        configureMap(dataSource){
            if ( // Check dataSource format
                dataSource["data"] !== undefined &&
                dataSource.data["features"] !== undefined &&
                dataSource.data.features.length > 0
            ) {
                this.setDataSource(dataSource);
                this.drawClustered();
                this.drawUnclustered();
                this.initPopups();
                this.map.triggerRepaint();
            } //else throw "Wrong data source formta provider. See https://docs.mapbox.com/mapbox-gl-js/api/map/#map#addsource";
        },
        setDataSource(dataSource) {
            // Set dataSource or update data, if dataSource already exists
            if (this.map.getSource('locations')) this.map.getSource('locations').setData(dataSource.data);
            else { this.map.addSource('locations', dataSource); }
        },
        drawClustered() {
            // Create points and values labels for clustered locations
            this.map.addLayer({
                id: 'chats-clustered-locations',
                type: 'circle',
                source: 'locations',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        20, 100, 30, 750, 40
                    ],
                    'circle-color': this.colors.dddark,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': this.colors.ddwhite
                }
            });

            this.map.addLayer({
                id: 'chats-clustered-count',
                type: 'symbol',
                source: 'locations',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{total_chats}',
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12
                },
                paint: { 'text-color': this.colors.ddwhite }
            });
        },
        drawUnclustered() {
            // Create points and values labels for unclustered locations
            this.map.addLayer({
                id: 'chats-unclustered-locations',
                type: 'circle',
                source: 'locations',
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'circle-radius': 10,
                    'circle-color': this.colors.dddark,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': this.colors.ddwhite
                }
            });

            this.map.addLayer({
                id: 'chats-unclustered-count',
                type: 'symbol',
                source: 'locations',
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'text-field': '{chats}',
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 10
                },
                paint: { 'text-color': this.colors.ddwhite }
            });
        },
        initPopups() {
            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                className: "popup"
            });

            const popupUnclustered = e => {
                this.map.getCanvas().style.cursor = 'pointer';
                const coordinates = e.features[0].geometry.coordinates.slice();

                // Place tooltip on point top or bottom based on map position 
                // frame.
                while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                }

                const { title, chats } = e.features[0].properties;
                popup.setLngLat(coordinates).setHTML(`${title}: ${chats} chats`).addTo(this.map);
            };

            const popupClustered = e => {
                this.map.getCanvas().style.cursor = 'pointer';
                const coordinates = e.features[0].geometry.coordinates.slice();

                // Place tooltip on point top or bottom based on map position 
                // frame.
                while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                }

                const { total_chats, point_count } = e.features[0].properties;
                popup.setLngLat(coordinates).setHTML(`${total_chats} in ${point_count} locations.`).addTo(this.map);
            };

            const popupRemover = () => {
                this.map.getCanvas().style.cursor = '';
                //popup.remove();
            }

            // Listen 'mouseenter' event over unclusetered points layer
            this.map.on('mouseenter', 'chats-unclustered-locations', popupUnclustered);
            this.map.on('mouseenter', 'chats-clustered-locations', popupClustered);
            this.map.on('mouseleave', 'chats-unclustered-count', popupRemover);
            this.map.on('mouseleave', 'chats-clustered-count', popupRemover);
        }
    },
    watch: {
        data(newData) { this.setDataSource(newData) }, // Update dataSource when data changes
        dataSource(newDataSource) { // Listen for prop updates and fire a data change if mapbox is already loaded
            if (typeof this.map["loaded"] === 'function') {
                if (this.map.loaded() && this.map.getSource('locations')) this.data = newDataSource;
                else this.initMap(newDataSource);
            }
        }
    }
}
</script>

<style>
    #map {
        display: inline-block;
        vertical-align: top;
        width: 100%;
        height: 100%;
        min-height: 420px;
    }

    .mapboxgl-popup-content {
        color: black !important;
    }
</style>